import React, { type ReactNode } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/ui/breadcrumb';
import { Separator } from '@/ui/separator';
import { SidebarTrigger, useSidebar } from '@/ui/sidebar';

interface DashboardBreadcrumbItem {
  href?: string;
  label: string;
  isCurrentPage?: boolean;
}

interface DashboardHeaderProps {
  titleContent?: ReactNode;
  breadcrumbs: DashboardBreadcrumbItem[];
  actions?: ReactNode;
  onNavigate?: (path: string) => void;
}

export function DashboardHeader({
  breadcrumbs,
  actions,
  titleContent,
  onNavigate,
}: DashboardHeaderProps) {
  const { isMobile } = useSidebar();

  return (
    <header className="shrink-0 border-b bg-background/95 transition-[width,height] ease-linear backdrop-blur supports-[backdrop-filter]:bg-background/80 group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full items-center gap-2 px-4 py-2.5 lg:px-6">
        <SidebarTrigger className="-ml-1 shrink-0" />

        {!isMobile && breadcrumbs.length > 0 ? (
          <>
            <Separator
              orientation="vertical"
              className="data-[orientation=vertical]:h-4"
            />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((item, index) => (
                  <React.Fragment key={`breadcrumb-${index}`}>
                    {index > 0 && <BreadcrumbSeparator key={`sep-${index}`} />}
                    <BreadcrumbItem key={`item-${index}`}>
                      {item.isCurrentPage ? (
                        <BreadcrumbPage>{item.label}</BreadcrumbPage>
                      ) : item.href ? (
                        <BreadcrumbLink
                          href={item.href}
                          onClick={(event) => {
                            if (!onNavigate) {
                              return;
                            }

                            event.preventDefault();
                            onNavigate(item.href!);
                          }}
                        >
                          {item.label}
                        </BreadcrumbLink>
                      ) : onNavigate ? (
                        <BreadcrumbLink
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                          }}
                        >
                          {item.label}
                        </BreadcrumbLink>
                      ) : (
                        <span className="text-muted-foreground">
                          {item.label}
                        </span>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </>
        ) : null}

        {titleContent ? (
          <div className="min-w-0 shrink">{titleContent}</div>
        ) : null}

        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
          {actions}
        </div>
      </div>
    </header>
  );
}
